import jStat from 'jstat';

import map from 'lodash/map';
import get from 'lodash/get';
import times from 'lodash/times';
import random from 'lodash/random';
import indexOf from 'lodash/indexOf';
import shuffle from 'lodash/shuffle';

import { change } from 'redux-form';
import { createSelector } from 'reselect';

import { round4 } from 'utilities/math';

import { RANDOMIZATION_HYPOTHESIS_TESTING_FORM } from 'store/form';

import {
  getSelectedParameters,
  getSelectedParameterType,
  getFilteredParameterValuesNotEmpty,
  getFilteredParametersValuesNotEmpty
} from 'store/dataset';

import {
  getNullValue as getOldNullValue
} from 'store/scenes/randomizationHypothesisTesting/nullValue/selectors';

import {
  getPrimaryParameterLevel as getOldPrimaryParameterLevel
} from 'store/scenes/randomizationHypothesisTesting/primaryParameterLevel/selectors';

import {
  getTestSelection as getOldTestSelection
} from 'store/scenes/randomizationHypothesisTesting/testSelection/selectors';

import { setNullValue } from 'store/scenes/randomizationHypothesisTesting/nullValue/actions';

import {
  getPrimaryParameter as getOldPrimaryParameter
} from 'store/scenes/randomizationHypothesisTesting/primaryParameter/selectors';

import {
  getSecondaryParameter as getOldSecondaryParameter
} from 'store/scenes/randomizationHypothesisTesting/secondaryParameter/selectors';

import {
  setPrimaryParameter
} from 'store/scenes/randomizationHypothesisTesting/primaryParameter/actions';

import {
  setSecondaryParameter
} from 'store/scenes/randomizationHypothesisTesting/secondaryParameter/actions';

import { setTestSelection } from './testSelection';
import { setPrimaryParameterLevel } from './primaryParameterLevel';

import {
  setSamples,
  appendSamples
} from 'store/scenes/randomizationHypothesisTesting/samples/actions';

import {
  getSamplesMean,
  getSamplesStdev
} from 'store/scenes/randomizationHypothesisTesting/samples/selectors';

import * as types from './types';

import {
  getAlpha,
  getTailType,
  getNumberOfSamples,
  getPrimaryParameterType,
  isSecondaryParameterSelected,

  getNullValue as getNewNullValue,
  getTestSelection as getNewTestSelection,
  getPrimaryParameterName as getNewPrimaryParameterName,
  getPrimaryParameterLevel as getNewPrimaryParameterLevel,
  getSecondaryParameterName as getNewSecondaryParameterName
} from 'store/form/randomizationHypothesisTesting';

export const setTestSelectionType = parameterName => (dispatch, getState) => {
  const state = getState();

  const previousPrimaryParameterType = getPrimaryParameterType(state);
  const newPrimaryParameterType = getSelectedParameterType(state, parameterName);

  const secondaryParameterSelected = isSecondaryParameterSelected(state);

  if (newPrimaryParameterType !== previousPrimaryParameterType || secondaryParameterSelected) {
    dispatch(change(RANDOMIZATION_HYPOTHESIS_TESTING_FORM, 'testSelection',
      newPrimaryParameterType === 'numerical'
        ? 'mean'
        : 'proportion'
    ));
  }
};

const setCutoffs = () => (dispatch, getState) => {
  const state = getState();

  const mean = getSamplesMean(state);
  const stdev = getSamplesStdev(state);

  const tailType = getTailType(state);

  const alpha = tailType === 'both'
    ? getAlpha(state) / 2
    : getAlpha(state);

  const cutoffs = {
    leftCutoff: round4(jStat.normal.inv(alpha, mean, stdev)),
    rightCutoff: round4(jStat.normal.inv(1 - alpha, mean, stdev))
  };

  dispatch(
    map(cutoffs, (cutoffValue, cutoffName) =>
      change(
        RANDOMIZATION_HYPOTHESIS_TESTING_FORM,
        cutoffName,
        cutoffValue
      )
    )
  );
};

const makeNewParameterSelector = parameterNameSelector =>
  createSelector(getSelectedParameters, parameterNameSelector,
    (selectedParameters, parameterName) => {
      const type = get(selectedParameters[parameterName], 'targetType');

      return { name: parameterName, type };
    }
  );

const getNewPrimaryParameter = makeNewParameterSelector(getNewPrimaryParameterName);
const getNewSecondaryParameter = makeNewParameterSelector(getNewSecondaryParameterName);

export const bootstrapSamples = () => (dispatch, getState) => {
  const state = getState();

  const newPrimaryParameter = getNewPrimaryParameter(state);
  const newSecondaryParameter = getNewSecondaryParameter(state);

  const oldPrimaryParameter = getOldPrimaryParameter(state);
  const oldSecondaryParameter = getOldSecondaryParameter(state);
  const secondaryParameterSelected = isSecondaryParameterSelected(state);

  if (!secondaryParameterSelected) {
    newPrimaryParameter.values = getFilteredParameterValuesNotEmpty(state, newPrimaryParameter.name);
    newSecondaryParameter.values = [];
  } else {
    const { firstParsedParameterValues, secondParsedParameterValues } = getFilteredParametersValuesNotEmpty(state, newPrimaryParameter.name, newSecondaryParameter.name);

    newPrimaryParameter.values = firstParsedParameterValues;
    newSecondaryParameter.values = secondParsedParameterValues;
  }
  const newNullValue = getNewNullValue(state);
  const oldNullValue = getOldNullValue(state);

  const newPrimaryParameterLevel = getNewPrimaryParameterLevel(state);
  const oldPrimaryParameterLevel = getOldPrimaryParameterLevel(state);

  const newTestSelection = getNewTestSelection(state);
  const oldTestSelection = getOldTestSelection(state);

  const numberOfSamples = getNumberOfSamples(state);

  const shift =
    newTestSelection === 'mean'
      ? newNullValue - jStat.mean(newPrimaryParameter.values)
      : 0;

  const getLevelWithProbability = () => {
    const p =
      newTestSelection === 'count'
        ? newNullValue / newPrimaryParameter.values.length
        : newNullValue;

    return Math.random() < p
      ? (
        indexOf(
          newPrimaryParameter.values,
          newPrimaryParameterLevel
        )
      )
      : null;
  };

  const samples = times(numberOfSamples, () => {
    const indexes = times(newPrimaryParameter.values.length, () =>
      newTestSelection === 'mean' || newTestSelection === 'slope' || newTestSelection === 'correlation'
        ? random(newPrimaryParameter.values.length - 1)
        : getLevelWithProbability()
    );

    const shuffledIndexes = shuffle(indexes);

    return times(indexes.length, i => {
      if (secondaryParameterSelected) {
        return [
          newPrimaryParameter.values[indexes[i]],
          newSecondaryParameter.values[shuffledIndexes[i]]
        ];
      }

      return newTestSelection === 'mean'
        ? newPrimaryParameter.values[indexes[i]] + shift
        : newPrimaryParameter.values[indexes[i]];
    });
  });

  const samePrimaryParameter = newPrimaryParameter === oldPrimaryParameter;
  const sameSecondaryParameter = newSecondaryParameter === oldSecondaryParameter;
  const samePrimaryParameterLevel = newPrimaryParameterLevel === oldPrimaryParameterLevel;

  const sameNullValue = newNullValue === oldNullValue;
  const sameTestSelection = newTestSelection === oldTestSelection;

  dispatch([
    ...(!samePrimaryParameter ? [setPrimaryParameter(newPrimaryParameter)] : []),
    ...(!sameSecondaryParameter ? [setSecondaryParameter(newSecondaryParameter)] : []),
    ...(!samePrimaryParameterLevel ? [setPrimaryParameterLevel(newPrimaryParameterLevel)] : []),

    ...(!sameNullValue ? [setNullValue(newNullValue)] : []),
    ...(!sameTestSelection ? [setTestSelection(newTestSelection)] : []),

    (samePrimaryParameter && sameSecondaryParameter && sameNullValue && samePrimaryParameterLevel && sameTestSelection)
      ? appendSamples(samples)
      : setSamples(samples)
  ]);

  dispatch(setCutoffs());
};

export const resetRandomizationHypothesisTesting = () => ({
  type: types.RESET_RANDOMIZATION_HYPOTHESIS_TESTING
});
